import { BepiCategory } from 'client/dto';

export type Vulnerability = 'low' | 'moderate' | 'high';

export const LowVulnerabilityThreshold = 75;
export const HighVulnerabilityThreshold = 39;
export const LocationResiliencyToVulnerability: (locationResiliency: number) => Vulnerability = (
  locationResiliency: number
) => {
  if (locationResiliency == null) {
    return null;
  }
  if (locationResiliency >= LowVulnerabilityThreshold) {
    return 'low';
  }
  if (locationResiliency > HighVulnerabilityThreshold) {
    return 'moderate';
  }
  return 'high';
};

export type BepiCategoryDescription = {
  label: string;
  consideredFactors: string[];
  descriptionLines: string[];
};

export const BepiCategoryDescriptions: Record<BepiCategory, BepiCategoryDescription> = {
  [BepiCategory.Demographics]: {
    label: 'Demographic',
    consideredFactors: [
      'Population density',
      'Urbanization',
      'Population growth',
      'Literacy',
      'Population mobility',
    ],
    descriptionLines: [
      'This domain approximates the role of a location’s population density, growth and sociodemographic factors in the degree of vulnerability to infectious disease outbreaks.',
      'States with densely packed, fast-growing urban areas and high population mobility across borders are scored as more vulnerable to the spread of contagious diseases. States with higher education and literacy rates are associated with adoption of health behaviours or practices that reduce disease transmission and are scored as less vulnerable to the spread of contagious diseases.',
    ],
  },
  [BepiCategory.Healthcare]: {
    label: 'Health Care System',
    consideredFactors: [
      'Medical care workforce (doctors and nurses)',
      'Health care expenditures',
      'Health care infrastructure',
    ],
    descriptionLines: [
      'This domain approximates the role of a location’s health care system, expenditure, and infrastructure in the degree of vulnerability to infectious disease outbreaks.',
      'The strength and quality of a nation’s health care system has direct bearing on resiliency to infectious disease outbreaks is it relates to access to care, timely and adequate treatment, and prevention of secondary infections among transmissible pathogens.',
    ],
  },
  [BepiCategory.PublicHealth]: {
    label: 'Public Health',
    consideredFactors: [
      'Public health service delivery and infrastructure',
      'Water/sanitation/hygiene',
      'IHR core capacity',
      'GHSA action packages',
    ],
    descriptionLines: [
      'This domain approximates the role of a location’s public health infrastructure in the degree of vulnerability to infectious disease outbreaks.',
      'The ability of a government to deliver basic public health services (such as vaccinations) and the proportion of the population with access to clean water and improved sanitation facilities reflect how well communities can prevent or respond to disease threats. IHR and GHSA measures are indicative of the public health capacity to detect and respond to infectious or biological threats.',
    ],
  },
  [BepiCategory.DiseaseDynamics]: {
    label: 'Disease Dynamics',
    consideredFactors: ['Temperature', 'Precipitation', 'Changes in land-use', 'Deforestation'],
    descriptionLines: [
      'This domain approximates the role of a location’s ecological conditions in the degree of vulnerability to infectious disease outbreaks.',
      'Patterns of precipitation and temperature can directly affect disease transmission through impacts on the replication and movement (and perhaps evolution) of disease microbes and vectors giving rise to water- or vector-borne diseases, such as cholera, malaria, dengue, and West Nile virus. Furthermore, increases in anthropogenic activities—such as changes in the national patterns of land use, including the extent of agricultural activities and deforestation—are associated with the likelihood of emergence of zoonotic infectious diseases.',
    ],
  },
  [BepiCategory.PoliticalDomestic]: {
    label: 'Political (Domestic)',
    consideredFactors: [
      'Domestic governance',
      'Corruption',
      'Service provision',
      'Decentralization',
      'Democracy',
      'Stability',
      'Conflict',
      'Human rights',
    ],
    descriptionLines: [
      'This domain approximates the role of a location’s national governance status in the degree of vulnerability to infectious disease outbreaks.',
      'Stable governments without conflicts within their borders are also likely to be more resilient in this regard, as are governments that are perceived as providing quality services to their citizens. Greater levels of democracy within a country and empowerment of local government has historically yielded more-capable organization and response at the city, state, or province level in many circumstances.',
    ],
  },
  [BepiCategory.PoliticalInternational]: {
    label: 'Political (International)',
    consideredFactors: [
      'Aid support',
      'Dependence',
      'Continuity',
      'International support',
      'Bilateral support',
      'Collaboration',
    ],
    descriptionLines: [
      'This domain approximates the role of a location’s cooperation and collaboration with international organizations in the degree of vulnerability to infectious disease outbreaks.',
      'The consistent support of both bilateral donors and international organizations (e.g. the WHO; the U.S. Centers for Disease Control and Prevention (CDC); and various nongovernmental organizations (NGOs), such as Médecins Sans Frontières (Doctors Without Borders), etc.) can help strengthen a country’s health system and provide critical funding, expertise, and personnel in preparing for and responding to health crises. Transparency and international collaboration facilitate timely responses to disease threats between countries.',
    ],
  },
  [BepiCategory.Economy]: {
    label: 'Economic',
    consideredFactors: [
      'Economic strength',
      'Growth',
      'Development and transportation',
      'Technology',
      'Communications infrastructure',
    ],
    descriptionLines: [
      'This domain approximates the role of a location’s economic stability in the degree of vulnerability to infectious disease outbreaks.',
      'The size and scope of a country’s economy often dictates the amount and quality of resources it has to prepare for and respond to the threats posed by infectious diseases. Certain measures of economic strength and development indicate higher resilience, including higher GDP per capita, economic growth rates, the Human Development Index (from the United Nations Development Programme), and lower national poverty ratios.',
    ],
  },
  [BepiCategory.BEPIOverall]: null,
};
